/**
 * PostgreSQL v2 API Utility
 * 
 * This utility is for components that have been migrated to use PostgreSQL v2 endpoints.
 * Use this instead of the regular api utility for migrated components.
 * 
 * All calls automatically:
 * - Add JWT token from localStorage
 * - Use v2 endpoint prefix (/api/v2/...)
 * - Include organization context
 */
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

// Create axios instance with v2 base URL
const apiV2 = axios.create({
  baseURL: `${API_URL}/v2`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token and org context
apiV2.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem('jwtToken');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add organization ID for org-scoped requests
    const orgId = localStorage.getItem('organizationId');
    if (orgId) {
      config.headers['X-Organization-Id'] = orgId;
    }
    
    // Add organization slug header as fallback
    const orgSlug = localStorage.getItem('organizationSlug');
    if (orgSlug) {
      config.headers['X-Organization-Slug'] = orgSlug;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiV2.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 403 Forbidden - Permission denied
    if (error.response && error.response.status === 403) {
      // Check if this is a background data fetch (GET request to a list endpoint)
      const isBackgroundFetch = error.config?.method?.toLowerCase() === 'get' && 
        (error.config?.url?.includes('/bookings') || 
         error.config?.url?.includes('/team-members') ||
         error.config?.url?.includes('/assets') ||
         error.config?.url?.includes('/services') ||
         error.config?.url?.includes('/roles'));
      
      // Only show toast for user-initiated actions, not background fetches
      if (!isBackgroundFetch) {
        const message = error.response.data?.detail || "Your role doesn't have permission for this action";
        toast.error(message, {
          duration: 4000,
          icon: '🔒'
        });
      }
    }
    
    // Handle 401 Unauthorized
    if (error.response && error.response.status === 401) {
      const requestUrl = error.config?.url || '';
      const currentPath = window.location.pathname;
      
      // Routes that should NOT auto-redirect to login on 401
      const skipRedirectRoutes = [
        '/public/',
        '/customer-ticket',
        '/ticket/',
        '/check-in/',
        '/self-check-in',
        '/book',
        '/booking',
        '/widget',
        '/account/'  // Account management routes should not auto-logout
      ];
      
      const shouldSkipRedirect = skipRedirectRoutes.some(route => 
        requestUrl.includes(route) || currentPath.includes(route)
      );
      
      if (!shouldSkipRedirect) {
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('userType');
        localStorage.removeItem('userData');
        localStorage.removeItem('organizationId');
        localStorage.removeItem('organizationSlug');
        
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

export default apiV2;

// Export convenience methods with org context
export const getOrgId = () => localStorage.getItem('organizationId');

// Organization-scoped convenience methods
export const apiV2Get = (url, config) => apiV2.get(url, config);
export const apiV2Post = (url, data, config) => apiV2.post(url, data, config);
export const apiV2Put = (url, data, config) => apiV2.put(url, data, config);
export const apiV2Patch = (url, data, config) => apiV2.patch(url, data, config);
export const apiV2Delete = (url, config) => apiV2.delete(url, config);

// Org-specific endpoints
export const orgApi = {
  // Organization info - uses auth endpoint for current org
  getOrganization: () => apiV2.get('/auth/organizations/me'),
  updateOrganization: (data) => apiV2.patch('/auth/organizations/me', data),
  
  // Services
  getServices: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.activeOnly !== undefined) queryParams.append('activeOnly', params.activeOnly);
    if (params.includeAssets) queryParams.append('includeAssets', params.includeAssets);
    const queryString = queryParams.toString();
    return apiV2.get(`/organizations/${getOrgId()}/services${queryString ? '?' + queryString : ''}`);
  },
  getService: (id) => apiV2.get(`/organizations/${getOrgId()}/services/${id}`),
  createService: (data) => apiV2.post(`/organizations/${getOrgId()}/services`, data),
  updateService: (id, data) => apiV2.patch(`/organizations/${getOrgId()}/services/${id}`, data),
  deleteService: (id) => apiV2.delete(`/organizations/${getOrgId()}/services/${id}`),
  reorderServices: (services) => apiV2.put(`/organizations/${getOrgId()}/services/reorder`, { services }),
  reactivateService: (id) => apiV2.post(`/organizations/${getOrgId()}/services/${id}/reactivate`),
  permanentDeleteService: (id) => apiV2.delete(`/organizations/${getOrgId()}/services/${id}/permanent`),
  
  // Assets
  getAssets: () => apiV2.get(`/organizations/${getOrgId()}/assets`),
  getAsset: (id) => apiV2.get(`/organizations/${getOrgId()}/assets/${id}`),
  createAsset: (data) => apiV2.post(`/organizations/${getOrgId()}/assets`, data),
  updateAsset: (id, data) => apiV2.patch(`/organizations/${getOrgId()}/assets/${id}`, data),
  deleteAsset: (id) => apiV2.delete(`/organizations/${getOrgId()}/assets/${id}`),
  
  // Team Members
  getTeamMembers: () => apiV2.get(`/organizations/${getOrgId()}/team-members`),
  getTeamMember: (id) => apiV2.get(`/organizations/${getOrgId()}/team-members/${id}`),
  createTeamMember: (data) => apiV2.post(`/organizations/${getOrgId()}/team-members`, data),
  updateTeamMember: (id, data) => apiV2.patch(`/organizations/${getOrgId()}/team-members/${id}`, data),
  deleteTeamMember: (id) => apiV2.delete(`/organizations/${getOrgId()}/team-members/${id}`),
  sendTeamMemberInvite: (id) => apiV2.post(`/organizations/${getOrgId()}/team-members/${id}/send-invite`),
  resendTeamMemberVerification: (id) => apiV2.post(`/organizations/${getOrgId()}/team-members/${id}/resend-verification`),
  
  // Bookings
  getBookings: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.status) queryParams.append('status', params.status);
    const queryString = queryParams.toString();
    return apiV2.get(`/organizations/${getOrgId()}/bookings${queryString ? '?' + queryString : ''}`).then(res => res.data);
  },
  getBooking: (id) => apiV2.get(`/organizations/${getOrgId()}/bookings/${id}`),
  createBooking: (data) => apiV2.post(`/organizations/${getOrgId()}/bookings`, data),
  
  // Internal booking with Stripe checkout (v2)
  createInternalBookingWithCheckout: (data) => apiV2.post(`/organizations/${getOrgId()}/internal-booking/create-with-checkout`, data),
  
  // Generic post for org endpoints
  post: (path, data) => apiV2.post(`/organizations/${getOrgId()}${path}`, data),
  updateBooking: (id, data) => apiV2.patch(`/organizations/${getOrgId()}/bookings/${id}`, data),
  cancelBooking: (id) => apiV2.post(`/organizations/${getOrgId()}/bookings/${id}/cancel`),
  checkInBooking: (id) => apiV2.post(`/organizations/${getOrgId()}/bookings/${id}/check-in`),
  
  // AI Smart Scheduling
  getAIScheduleSuggestion: (bookingId) => apiV2.post(`/organizations/${getOrgId()}/bookings/${bookingId}/ai-schedule-suggestion`),
  getBulkScheduleSuggestions: (bookingIds) => apiV2.post(`/organizations/${getOrgId()}/bulk-schedule-suggestions`, { booking_ids: bookingIds }),
  applyBulkSchedule: (assignments) => apiV2.post(`/organizations/${getOrgId()}/bulk-apply-schedule`, { assignments }),
  
  // Calendar
  getCalendar: (startDate, endDate) => apiV2.get(`/organizations/${getOrgId()}/calendar?startDate=${startDate}&endDate=${endDate}`),
  
  // Payments
  getPaymentSummary: (startDate, endDate) => apiV2.get(`/organizations/${getOrgId()}/payments/summary?startDate=${startDate}&endDate=${endDate}`),
  getPartners: () => apiV2.get(`/organizations/${getOrgId()}/partners`),
  getPromoCodes: () => apiV2.get(`/organizations/${getOrgId()}/promo-codes`),
  
  // Customers
  getCustomers: () => apiV2.get(`/organizations/${getOrgId()}/customers`),
  getCustomer: (id) => apiV2.get(`/organizations/${getOrgId()}/customers/${id}`),
  
  // Roles
  getRoles: () => apiV2.get(`/organizations/${getOrgId()}/roles`),
  getRole: (id) => apiV2.get(`/organizations/${getOrgId()}/roles/${id}`),
  createRole: (data) => apiV2.post(`/organizations/${getOrgId()}/roles`, data),
  updateRole: (id, data) => apiV2.patch(`/organizations/${getOrgId()}/roles/${id}`, data),
  deleteRole: (id) => apiV2.delete(`/organizations/${getOrgId()}/roles/${id}`),
  
  // Financials (v2 PostgreSQL)
  getFinancialSummary: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.period) queryParams.append('period', params.period);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    const queryString = queryParams.toString();
    return apiV2.get(`/organizations/${getOrgId()}/financials/summary${queryString ? '?' + queryString : ''}`);
  },
  getFinancialBalance: () => apiV2.get(`/organizations/${getOrgId()}/financials/balance`),
  getFinancialBookings: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.period) queryParams.append('period', params.period);
    if (params.paymentMethod) queryParams.append('paymentMethod', params.paymentMethod);
    if (params.paymentStatus) queryParams.append('paymentStatus', params.paymentStatus);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    const queryString = queryParams.toString();
    return apiV2.get(`/organizations/${getOrgId()}/financials/bookings${queryString ? '?' + queryString : ''}`);
  },
  getRevenueChart: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.period) queryParams.append('period', params.period);
    if (params.groupBy) queryParams.append('groupBy', params.groupBy);
    const queryString = queryParams.toString();
    return apiV2.get(`/organizations/${getOrgId()}/financials/revenue-chart${queryString ? '?' + queryString : ''}`);
  },
  getPayoutRequests: () => apiV2.get(`/organizations/${getOrgId()}/financials/payout-requests`),
  recordPayment: (data) => apiV2.post(`/organizations/${getOrgId()}/financials/record-payment`, data),
  
  // Expenses
  getExpenses: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.category) queryParams.append('category', params.category);
    const queryString = queryParams.toString();
    return apiV2.get(`/organizations/${getOrgId()}/expenses${queryString ? '?' + queryString : ''}`).then(res => res.data);
  },
  createExpense: (data) => apiV2.post(`/organizations/${getOrgId()}/expenses`, data).then(res => res.data),
  updateExpense: (id, data) => apiV2.put(`/organizations/${getOrgId()}/expenses/${id}`, data).then(res => res.data),
  deleteExpense: (id) => apiV2.delete(`/organizations/${getOrgId()}/expenses/${id}`).then(res => res.data),
};

// Super Admin endpoints (for super admin dashboard)
export const superAdminApi = {
  login: (username, password) => apiV2.post('/super-admin/auth/login', { username, password }),
  getOrganizations: () => apiV2.get('/super-admin/organizations'),
  getOrganization: (id) => apiV2.get(`/super-admin/organizations/${id}`),
  approveOrganization: (id) => apiV2.post(`/super-admin/organizations/${id}/approve`),
  suspendOrganization: (id) => apiV2.post(`/super-admin/organizations/${id}/suspend`),
  activateOrganization: (id) => apiV2.post(`/super-admin/organizations/${id}/activate`),
  updateOrganization: (id, data) => apiV2.patch(`/super-admin/organizations/${id}`, data),
  adjustActions: (id, amount, reason) => apiV2.post(`/super-admin/organizations/${id}/actions`, { amount, reason }),
  getPlatformStats: () => apiV2.get('/super-admin/platform-stats'),
  getPlatformFinancials: (period = 'all') => apiV2.get(`/super-admin/platform-financials?period=${period}`),
  getActivityLogs: (limit = 100) => apiV2.get(`/super-admin/activity-logs?limit=${limit}`),
};
